import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  getDoc,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Observable, firstValueFrom, of } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { GeoLocation, Location, LocationInput } from './location.model';
import { CurrentUserService } from '../../shared/services/current-user.service';

const LOCATIONS_COLLECTION = 'locations';

@Injectable({ providedIn: 'root' })
export class LocationsService {
  readonly #firestore = inject(Firestore);
  readonly #currentUser = inject(CurrentUserService);

  #collection() {
    return collection(this.#firestore, LOCATIONS_COLLECTION);
  }

  #docRef(id: string) {
    return doc(this.#firestore, LOCATIONS_COLLECTION, id);
  }

  /** Live list of the current user's company locations, sorted by name. */
  readonly locations$: Observable<Location[]> = this.#currentUser.company$.pipe(
    switchMap((company) => {
      if (!company) return of([]);

      const locationsQuery = query(
        this.#collection(),
        where('companyId', '==', company.companyId),
        orderBy('name', 'asc'),
      );
      return collectionData(locationsQuery, { idField: 'id' }) as Observable<Location[]>;
    }),
    // One listener shared by all pages; stops when nobody subscribes (e.g. after logout)
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Returns undefined if the location doesn't exist or belongs to another company. */
  async getLocation(id: string): Promise<Location | undefined> {
    const [snapshot, company] = await Promise.all([
      getDoc(this.#docRef(id)),
      this.#currentUser.requireCompany(),
    ]);
    if (!snapshot.exists()) return undefined;

    const location = { id: snapshot.id, ...snapshot.data() } as Location;
    return location.companyId === company.companyId ? location : undefined;
  }

  /**
   * Creates a location in the current user's company, ordered after its existing locations. Returns its id.
   * Exact coordinates are required: customers navigate to this location.
   */
  async createLocation(data: LocationInput & { geoLocation: GeoLocation }): Promise<string> {
    const [company, existing] = await Promise.all([
      this.#currentUser.requireCompany(),
      firstValueFrom(this.locations$), // reuses the companyId + name index
    ]);
    const order = Math.max(-1, ...existing.map((l) => l.order ?? -1)) + 1;

    const ref = doc(this.#collection());
    await setDoc(ref, { ...data, ...company, order, active: true });
    return ref.id;
  }

  async updateLocation(id: string, data: Partial<LocationInput>): Promise<void> {
    await updateDoc(this.#docRef(id), data);
  }

  /** Disabled locations stay in Firestore (orders reference them), but customers can't order there. */
  async setActive(id: string, active: boolean): Promise<void> {
    await updateDoc(this.#docRef(id), { active });
  }
}
